/* eslint-disable no-unused-vars */
import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useDebounce, getQueryString, useToggleState } from 'utils';
import { useComputed, useSignal, useSignalEffect } from '@preact/signals-react';
import { campaignReportsAPI } from 'services/campaignReports.service';
import { useParams } from 'react-router-dom';
import { campaignReportsActions } from 'store/actions';

const getDefaultOpenedGroups = (allColsArr) => {
  const res = [];
  const oneColIsSelected = allColsArr
    .filter(({ type }) => type === 'column')
    .some(({ isSelected }) => isSelected);
  const oneEventIsSelected = allColsArr
    .filter(({ type }) => type === 'event')
    .some(({ isSelected }) => isSelected);
  oneColIsSelected && res.push('Data');
  oneEventIsSelected && res.push('Events');
  return res;
};

const orderField = {
  colName: 'selectedOrder',
  eventId: 'orderId',
};

const transformDataToSave = (columnsArray, cmp_id, eventsArrToSave) => {
  const eventsArray = eventsArrToSave.map(
    ({ eventId, eventName, isDefaultSelected, isSelected, orderId }) => {
      return { colName: eventId, isSelected, label: eventName, selectedOrder: orderId };
    },
  );
  const columnsObj = {
    title1: 'Available columns',
    title2: 'Selected columns',
    cols: columnsArray.map(({ colName, isSelected, label, selectedOrder }) => {
      return { colName, isSelected, label, selectedOrder };
    }),
  };

  const dataToSave = {
    cmId: cmp_id,
    tableName: 'appReport',
    columns: JSON.stringify(columnsObj),
    events: JSON.stringify(eventsArray),
  };
  return dataToSave;
};

export const useFacade = ({ dateFrom$, dateTo$ }) => {
  const selectedCols = useSelector((state) => state.campaignReports.selectedCols);
  const selectedEvents = useSelector((state) => state.campaignReports.selectedEvents);
  const dispatch = useDispatch();
  const searchValue$ = useSignal('');
  const { cmp_id } = useParams();

  const currentRequestType = useSelector((state) => state.campaignReports.tableRequestTypes);
  const queryString = getQueryString(
    currentRequestType,
    { from: dateFrom$.value, to: dateTo$.value },
    cmp_id,
  );

  const [saveAction, result] = campaignReportsAPI.useSaveColumnsOrderMutation();

  const idToNameObject$ = useSignal({});
  const allColsArr$ = useSignal([]);
  const { data, isSuccess, isLoading, isError, isFetching } =
    campaignReportsAPI.useGetCampaignReportQuery(queryString, {
      selectFromResult: (response) => {
        if (response?.data?.selectedCols && response?.data?.cmEvents) {
          const allDataCols = JSON.parse(response.data.selectedCols).cols;
          const allColsArr = allDataCols.reduce((acc, col) => {
            const currentCol = structuredClone(col);
            if (currentCol.label === 'Viewable Impr.') currentCol.label = 'Viewable Impressions';
            if (selectedCols) {
              const index1 = selectedCols.indexOf(currentCol.colName);
              const isSelected = selectedCols.includes(currentCol.colName);
              currentCol.isSelected = isSelected;
              currentCol.selectedOrder = isSelected ? index1 + 1 : 0;
            }
            acc.push({ ...currentCol, type: 'column' });
            return acc;
          }, []);
          const events = !selectedEvents.length ? response.data.cmEvents : selectedEvents;
          events.forEach((event) => {
            allColsArr.push({ ...event, type: 'event' });
          });
          const idToNameObj = allColsArr.reduce((acc, col) => {
            if ('colName' in col) {
              acc[col.colName] = col.label;
            }
            if ('eventId' in col) {
              acc[col.eventId] = col.eventName;
            }
            return acc;
          }, {});
          return {
            ...response,
            data: { ...response.data, allColsArr, idToNameObj },
          };
        }
        return response;
      },
    });

  const {
    isActive: isActiveGroup,
    toggle: toggleGroup,
    toggleOn,
    toggleOff,
    state,
  } = useToggleState([]);

  useEffect(() => {
    if (data?.allColsArr && data?.idToNameObj) {
      allColsArr$.value = data.allColsArr;
      const openedGroups = getDefaultOpenedGroups(data.allColsArr);
      toggleOn(...openedGroups);
      idToNameObject$.value = data.idToNameObj;
    }
  }, [isFetching]);

  const isAllColsSelected$ = useComputed(() => {
    const allCols = allColsArr$.value.filter(({ type }) => type === 'column');
    return allCols.every(({ isSelected }) => isSelected);
  });

  const isAllEventsSelected$ = useComputed(() => {
    const allCols = allColsArr$.value.filter(({ type }) => type === 'event');
    return allCols.every(({ isSelected }) => isSelected);
  });

  const groupCheckBoxClick = (groupName) => {
    const newAllColsArr = [...allColsArr$.value];
    const isSelectedState =
      groupName === 'Data' ? isAllColsSelected$.value : isAllEventsSelected$.value;
    const itemType = groupName === 'Data' ? 'column' : 'event';
    newAllColsArr.forEach((item) => {
      if (item.type === itemType) {
        item.isSelected = !isSelectedState;
      }
    });
    allColsArr$.value = newAllColsArr;
  };

  const filteredColumns$ = useComputed(() => {
    if (!searchValue$.value) {
      return allColsArr$.value;
    }
    const searchResult = allColsArr$.value.filter((item, index, arr) => {
      if ('label' in item) {
        const res = item.label.toLowerCase().includes(searchValue$.value.toLowerCase());
        res && toggleOn('Data');
        return res;
      }
      if ('eventName' in item) {
        const res = item.eventName.toLowerCase().includes(searchValue$.value.toLowerCase());
        res && toggleOn('Events');
        return res;
      }
      return false;
    });
    return searchResult;
  });

  const getMaxOrderIndex = (fieldName) => {
    if (fieldName === 'colName') {
      const orderIndexColArray = rightSideColumnsArray$.value
        .filter(({ selectedOrder }) => selectedOrder > 0)
        .map(({ selectedOrder }) => selectedOrder);
      const maxColOrder = orderIndexColArray.reduce(
        (max, current) => (current > max ? current : max),
        0,
      );
      return { maxOrder: maxColOrder, selectedLength: orderIndexColArray.length };
    }
    if (fieldName === 'eventId') {
      const orderIndexEventArray = rightSideEventsArray$.value
        .filter(({ orderId }) => orderId > 0)
        .map(({ orderId }) => orderId);
      const maxEventOrder = orderIndexEventArray.reduce(
        (max, current) => (current > max ? current : max),
        0,
      );
      return { maxOrder: maxEventOrder, selectedLength: orderIndexEventArray.length };
    }
  };

  const handleColOrEventSelector = (colEventId, fieldName, type) => {
    const { maxOrder, selectedLength } = getMaxOrderIndex(fieldName);
    const newAllColsArr = structuredClone(allColsArr$.value);
    newAllColsArr.forEach((item) => {
      if (item[fieldName] === colEventId) {
        item.isSelected = !item.isSelected;
        item[orderField[fieldName]] = item.isSelected ? maxOrder + 1 : 0;
      }
    });
    const selectedCols = newAllColsArr.filter(
      (item) => item.type === type && item.isSelected,
    ).length;
    if (selectedCols === 0) return;
    allColsArr$.value = newAllColsArr;
  };

  const leftSideColumnsArray$ = useComputed(() => {
    return filteredColumns$.value.filter(({ type }) => type === 'column');
  });

  const leftSideEventsArray$ = useComputed(() => {
    return filteredColumns$.value.filter(({ type }) => type === 'event');
  });

  const rightSideColumnsArray$ = useComputed(() => {
    return allColsArr$.value.filter(({ type }) => type === 'column');
  });

  const rightSideEventsArray$ = useComputed(() => {
    return allColsArr$.value
      .filter(({ type }) => type === 'event')
      .map((eventObj, index) => {
        if (eventObj.orderId === null) {
          eventObj.orderId = index;
        }
        return eventObj;
      });
  });

  const rightSideSelectedCols$ = useSignal(null);
  const rightSideSelectedColLabels$ = useSignal(null);
  const rightSideSelectedEvents$ = useSignal(null);

  useSignalEffect(() => {
    rightSideSelectedCols$.value = rightSideColumnsArray$.value
      .filter(({ isSelected }) => isSelected)
      .sort((a, b) => a.selectedOrder - b.selectedOrder)
      .map(({ colName }) => colName);
    rightSideSelectedColLabels$.value = rightSideColumnsArray$.value
      .filter(({ isSelected }) => isSelected)
      .sort((a, b) => a.selectedOrder - b.selectedOrder)
      .map(({ label }) => label);
  });

  useSignalEffect(() => {
    rightSideSelectedEvents$.value = rightSideEventsArray$.value
      .filter(({ isSelected }) => isSelected)
      .sort((a, b) => a.orderId - b.orderId)
      .map(({ eventId }) => eventId);
  });

  const handleSave = async (isOpen$) => {
    const eventsArrToSave = structuredClone(
      rightSideEventsArray$.value.sort((a, b) => a.orderId - b.orderId),
    );
    dispatch(
      campaignReportsActions.updateProperty({
        propField: 'selectedEvents',
        newValue: eventsArrToSave,
      }),
    );
    const columnsListToSave = transformDataToSave(
      rightSideColumnsArray$.value,
      cmp_id,
      eventsArrToSave,
    );
    const saveResult = await saveAction(columnsListToSave);
    if (!Object.prototype.hasOwnProperty.call(saveResult, 'error')) {
      dispatch(
        campaignReportsActions.updateProperty({
          propField: 'selectedCols',
          newValue: rightSideSelectedCols$.value,
        }),
      );
      dispatch(
        campaignReportsActions.updateProperty({
          propField: 'selectedColLabels',
          newValue: rightSideSelectedColLabels$.value,
        }),
      );
      isOpen$.value = false;
    }
  };

  return {
    searchValue$,
    toggleGroup,
    isActiveGroup,
    isAllColsSelected$,
    groupCheckBoxClick,
    handleSave,
    handleColOrEventSelector,
    isAllEventsSelected$,
    leftSideColumnsArray$,
    leftSideEventsArray$,
    rightSideSelectedCols$,
    rightSideSelectedEvents$,
    allColsArr$,
    idToNameObject$,
  };
};
